import { useCallback, useEffect, useState } from "react";
import {
  Button,
  ConversationComponent,
  ConversationDialog,
  ConversationFilterPopover,
  ConversationsListItem,
  IconButton,
  Search,
  useIsMobile,
} from "@ac-expo/ui-components";
import { ChatCircleGes100Icon } from "../../icons";
import { ConversationListFilters } from "@ac-expo/api-types";
import { getFileUrl } from "../../shared/files";
import {
  useArchiveConversationMutation,
  useGetConversation,
  useGetConversationsList,
  useSendMessageMutation,
} from "@ac-expo/api";
import { searchByTerm } from "@ac-expo/utils";
import { useRouter } from "next/router";

function Index() {
  const router = useRouter();
  const { participantId } = router.query as { participantId: string };
  const isMobile = useIsMobile();

  const [participant, setParticipant] = useState<string | null>(
    participantId ?? null
  );
  const [searchText, setSearchText] = useState("");

  const [filter, setFilter] = useState({} as ConversationListFilters);

  const { data: conversationList, refetch: refetchList } =
    useGetConversationsList(filter);

  const {
    data: conversation,
    isLoading: conversationLoading,
    refetch: refetchConv,
  } = useGetConversation(participant ?? "ac-expo-admin");
  const { mutate: sendMessage } = useSendMessageMutation();
  const { mutate: mutateArchiveConversation } =
    useArchiveConversationMutation();

  const archiveConversation = (conversationId: string) => {
    mutateArchiveConversation(conversationId, {
      onSuccess: () => {
        refetchList();
        refetchConv();
      },
    });
  };

  useEffect(() => {
    refetchConv();
    refetchList();
  }, [refetchList, refetchConv, participant]);

  const conversations = conversationList?.filter(
    ({ with: { firstName, lastName } }) =>
      searchByTerm(searchText, `${firstName} ${lastName}`)
  );

  useEffect(() => {
    if (isMobile !== false) return;
    if (participantId) setParticipant(participantId);
    else if (conversationList && conversationList.length > 0) {
      setParticipant(conversationList[0].with.id);
    }
  }, [conversationList, participantId, isMobile]);

  const onContactConnectEvents = useCallback(() => {
    setParticipant("AC-EXPO");
  }, []);

  const onClickParticipant = useCallback(
    (participantId: string | null) => {
      setParticipant(participantId);
      refetchConv();
      router.replace(
        {
          pathname: router.pathname,
          query: { participantId },
        },
        undefined,
        { shallow: true }
      );
    },
    [participant]
  );

  if (isMobile) {
    return (
      <div className="flex h-full flex-col">
        <div>
          <div className="flex items-center justify-between p-6">
            <h1 className="text-2xl font-extrabold">Messagerie</h1>
            <IconButton
              svgType="lifeBuoy"
              svgProps={{ className: "text-primary-500" }}
              variant="hidden"
              onClick={onContactConnectEvents}
            />
          </div>
        </div>
        <div className="flex min-h-0 flex-1 md:border">
          <div className="flex w-full min-w-64 flex-col border border-t-0 md:w-3/12">
            <div className="flex items-center gap-4 border-b px-[14px] py-4">
              <div className="grow">
                <Search onChangeText={setSearchText} value={searchText} />
              </div>
            </div>
            <div className="relative size-full h-full overflow-y-auto">
              {!conversations || !conversations.length ? (
                <div className="flex size-full flex-col items-center justify-center gap-4 bg-gray-50 py-4">
                  <ChatCircleGes100Icon
                    width={20}
                    height={20}
                    fill={"#287BB9"}
                  />
                  <p className="text-base text-gray-500">
                    Aucune discussion pour le moment
                  </p>
                </div>
              ) : (
                conversations?.map((conversation) => (
                  <button
                    key={conversation.with.id}
                    onClick={() => onClickParticipant(conversation.with.id)}
                    className="w-full"
                  >
                    <ConversationsListItem
                      {...conversation}
                      archive={archiveConversation}
                      active={participant === conversation.with.id}
                      pictureUrl={getFileUrl(
                        conversation.with.picture?.key,
                        "media"
                      )}
                    />
                  </button>
                ))
              )}
            </div>
          </div>
          <ConversationDialog
            participant={participant ?? ""}
            setParticipant={setParticipant}
            conversation={conversation}
            sendMessage={sendMessage}
            pictureUrl={getFileUrl(conversation?.with.picture?.key, "media")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={"bg-white px-20 py-10"}>
      <div className={"flex justify-between pb-6"}>
        <h1 className="text-4xl font-bold">Messagerie</h1>
        <Button className="border shadow-none" onClick={onContactConnectEvents}>
          Contacter Connect&apos;Events
        </Button>
      </div>
      <div className={"flex h-[625px] border"}>
        <div className="h-full w-1/3 border">
          <div className={"flex items-center gap-4 border-b px-[14px] py-4"}>
            <div className="grow">
              <Search onChangeText={setSearchText} value={searchText} />
            </div>

            <ConversationFilterPopover filter={filter} setFilter={setFilter} />
          </div>
          <div className={"no-scrollbar h-[546px] overflow-y-auto"}>
            {conversation && !conversation.messages.length ? (
              <button
                key={conversation.with.id}
                onClick={() => onClickParticipant(conversation.with.id ?? "")}
                className={"w-full"}
              >
                <ConversationsListItem
                  archive={(id) => archiveConversation(id)}
                  id={conversation.id}
                  lastMessage={null}
                  with={conversation.with}
                  active={participant === conversation.with.id}
                  pictureUrl={getFileUrl(
                    conversation.with.picture?.key,
                    "media"
                  )}
                />
              </button>
            ) : null}
            {conversations?.map((conversation) => (
              <button
                key={conversation.with.id}
                onClick={() => onClickParticipant(conversation.with.id ?? "")}
                className={"w-full"}
              >
                <ConversationsListItem
                  id={conversation.id}
                  archive={(id) => archiveConversation(id)}
                  lastMessage={conversation.lastMessage}
                  with={conversation.with}
                  active={participant === conversation.with.id}
                  pictureUrl={getFileUrl(
                    conversation.with.picture?.key,
                    "media"
                  )}
                />
              </button>
            ))}
          </div>
        </div>
        <div className="flex h-full w-4/5">
          {!conversationLoading ? (
            <ConversationComponent
              conversation={conversation}
              sendMessage={sendMessage}
              pictureUrl={getFileUrl(conversation?.with.picture?.key, "media")}
            />
          ) : (
            <div className="h-full" />
          )}
        </div>
      </div>
    </div>
  );
}

export default Index;
